import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { WorshipReport } from '../../types/worshipReport';
import { X, Download } from 'lucide-react';
import { exportWorshipReportPdf } from '../../utils/exportPdf';
import { calculateTotalParticipants } from '../../utils/calculations';
import { useState, useEffect } from 'react';

interface WorshipReportPreviewProps {
  report: WorshipReport;
  onClose: () => void;
}

export function WorshipReportPreview({ report, onClose }: WorshipReportPreviewProps) {
  const totalParticipants = calculateTotalParticipants(report);
  const [speakerName, setSpeakerName] = useState('');

  useEffect(() => {
    const fetchSpeakerName = async () => {
      if (!report.speakerId) return;

      try {
        const speakerRef = collection(db, 'speakers');
        const q = query(speakerRef, where('__name__', '==', report.speakerId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setSpeakerName(querySnapshot.docs[0].data().name);
        }
      } catch (error) {
        console.error('Error fetching speaker:', error);
      }
    };
    fetchSpeakerName();
  }, [report.speakerId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Rapport de Culte</h2>
          <div className="flex items-center gap-2">
          <button
            onClick={() => exportWorshipReportPdf(report, speakerName)}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#00665C] text-white text-sm rounded-lg hover:bg-[#004d45] transition-colors"
            title="Télécharger en PDF"
          >
            <Download size={16} />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} />
          </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Informations générales */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium">{new Date(report.date).toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cellule</p>
                <p className="font-medium">{report.cellName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Église Source</p>
                <p className="font-medium">{report.sourceChurch}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Orateur</p>
                <p className="font-medium">{speakerName}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Thème</p>
                <p className="font-medium">{report.messageTheme}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Source</p>
                <p className="font-medium">{report.messageSource}</p>
              </div>
            </div>
          </section>

          {/* Statistiques */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Statistiques de présence</h3>
            <div className="mb-4 p-4 bg-[#00665C]/8 rounded-lg">
              <p className="text-[#004d45] font-semibold">Total des participants: {totalParticipants}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Adultes</h4>
                <p>Hommes: {report.attendance.adults.men}</p>
                <p>Femmes: {report.attendance.adults.women}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Enfants</h4>
                <p>Garçons: {report.attendance.children.boys}</p>
                <p>Filles: {report.attendance.children.girls}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Boss ayant servi</h4>
                <p>Hommes: {report.attendance.served.men}</p>
                <p>Femmes: {report.attendance.served.women}</p>
                <p>Blooms: {report.attendance.blooms}</p>
              </div>
            </div>
          </section>

          {/* Appel à conversion */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Appel à conversion</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Hommes</p>
                <p className="font-medium">{report.attendance.conversions?.men || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Femmes</p>
                <p className="font-medium">{report.attendance.conversions?.women || 0}</p>
              </div>
            </div>
          </section>

          {/* Nouveaux reçus */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Nouveaux reçus</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Hommes</p>
                <p className="font-medium">{report.newMembers.men}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Femmes</p>
                <p className="font-medium">{report.newMembers.women}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Blooms</p>
                <p className="font-medium">{report.newMembers.blooms}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Enfants</p>
                <p className="font-medium">{report.newMembers.children}</p>
              </div>
            </div>
          </section>

          {report.notes && (
            <section>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
              <p className="whitespace-pre-wrap">{report.notes}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}